import React, { useEffect, useId, useRef, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

const defaultLabel = (option) =>
  option ? `${option.firstName || ""} ${option.surName || ""}`.trim() : "";

const normalize = (value) => String(value || "").toLowerCase();

const matchesQuery = (option, query) => {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  return terms.every(
    (term) =>
      normalize(option.firstName).includes(term) ||
      normalize(option.surName).includes(term)
  );
};

export default function BeneficiarySearch({
  options = [],
  value = null,
  onChange,
  label,
  placeholder,
  disabled = false,
  noOptionsText = "No beneficiaries found",
  getOptionLabel = defaultLabel,
  getOptionSecondary,
  sx,
  limit = 100,
}) {
  const [query, setQuery] = useState(value ? getOptionLabel(value) : "");
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const anchorRef = useRef(null);
  const popperRef = useRef(null);
  const inputRef = useRef(null);
  const labelRef = useRef(getOptionLabel);
  const typingRef = useRef(false);
  const listboxId = useId();

  labelRef.current = getOptionLabel;

  useEffect(() => {
    if (value) {
      setQuery(labelRef.current(value));
    } else if (!typingRef.current) {
      setQuery("");
    }
    typingRef.current = false;
  }, [value]);

  const filtered = options.filter((option) => matchesQuery(option, query));
  const visible = filtered.slice(0, limit);

  const duplicateNames = new Set();
  const nameCounts = new Map();
  options.forEach((option) => {
    const key = labelRef.current(option).toLowerCase().replace(/\s+/g, " ").trim();
    if (!key) return;
    nameCounts.set(key, (nameCounts.get(key) || 0) + 1);
  });
  nameCounts.forEach((count, key) => {
    if (count > 1) duplicateNames.add(key);
  });

  useEffect(() => {
    setHighlighted(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    const option = document.getElementById(`${listboxId}-option-${highlighted}`);
    if (option) option.scrollIntoView({ block: "nearest" });
  }, [highlighted, open, listboxId]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (popperRef.current?.contains(event.target)) return;
      setOpen(false);
      setQuery(value ? labelRef.current(value) : "");
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open, value]);

  const select = (option) => {
    if (onChange) onChange(option);
    setQuery(labelRef.current(option));
    setOpen(false);
  };

  const clear = () => {
    if (onChange) onChange(null);
    setQuery("");
    setOpen(true);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleInputChange = (event) => {
    const next = event.target.value;
    setQuery(next);
    setOpen(true);
    if (value) {
      typingRef.current = true;
      if (onChange) onChange(null);
    }
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlighted((current) =>
        Math.min(current + 1, Math.max(visible.length - 1, 0))
      );
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted((current) => Math.max(current - 1, 0));
      return;
    }
    if (event.key === "Enter") {
      if (open && visible[highlighted]) {
        event.preventDefault();
        select(visible[highlighted]);
      }
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
      setQuery(value ? labelRef.current(value) : "");
    }
  };

  const handleBlur = (event) => {
    const next = event.relatedTarget;
    if (anchorRef.current?.contains(next)) return;
    if (popperRef.current?.contains(next)) return;
    setOpen(false);
    setQuery(value ? labelRef.current(value) : "");
  };

  const showClear = !disabled && (Boolean(value) || query.length > 0);

  return (
    <Box ref={anchorRef} sx={sx}>
      <TextField
        inputRef={inputRef}
        fullWidth
        label={label}
        placeholder={placeholder}
        value={query}
        onChange={handleInputChange}
        onFocus={() => {
          if (!disabled) setOpen(true);
        }}
        onClick={() => {
          if (!disabled) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={disabled}
        variant="outlined"
        inputProps={{
          role: "combobox",
          "aria-expanded": open,
          "aria-controls": listboxId,
          "aria-autocomplete": "list",
          "aria-activedescendant":
            open && visible[highlighted]
              ? `${listboxId}-option-${highlighted}`
              : undefined,
          autoComplete: "new-password",
          autoCorrect: "off",
          spellCheck: false,
          name: "beneficiary-search",
        }}
        InputProps={{
          endAdornment: showClear ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                edge="end"
                aria-label="Clear selection"
                onClick={clear}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      <Popper
        ref={popperRef}
        open={open && !disabled}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{
          width: anchorRef.current ? anchorRef.current.clientWidth : undefined,
          zIndex: 1300,
        }}
        modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
      >
        <Paper
          id={listboxId}
          role="listbox"
          elevation={8}
          sx={{ maxHeight: 320, overflowY: "auto" }}
        >
          {filtered.length === 0 ? (
            <MenuItem disabled>{noOptionsText}</MenuItem>
          ) : (
            visible.map((option, index) => {
              const name = labelRef.current(option);
              const key = name.toLowerCase().replace(/\s+/g, " ").trim();
              const secondary =
                duplicateNames.has(key) && getOptionSecondary
                  ? getOptionSecondary(option)
                  : "";
              return (
                <MenuItem
                  key={option.id ?? `${name}-${index}`}
                  id={`${listboxId}-option-${index}`}
                  role="option"
                  aria-selected={index === highlighted}
                  selected={index === highlighted}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => select(option)}
                  sx={{ display: "flex", gap: 2, justifyContent: "space-between" }}
                >
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {name}
                  </Box>
                  {secondary ? (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {secondary}
                    </Typography>
                  ) : null}
                </MenuItem>
              );
            })
          )}
        </Paper>
      </Popper>
    </Box>
  );
}
